import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Download, Upload, FileText, Lightbulb, ArrowRight, Search, File, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ResourcesPageProps {
  onOpenSampleModal?: () => void;
}
type FilterKey = 'all' | 'artwork' | 'technical' | 'templates' | 'blog';
const filters: {
  key: FilterKey;
  label: string;
}[] = [{
  key: 'all',
  label: 'All'
}, {
  key: 'artwork',
  label: 'Artwork guides'
}, {
  key: 'technical',
  label: 'Technical sheets'
}, {
  key: 'templates',
  label: 'Templates'
}, {
  key: 'blog',
  label: 'Blog & Insights'
}];
const artworkGuides = [{
  title: 'Artwork Setup Guide',
  format: 'PDF',
  size: '2.4 MB',
  description: 'Complete guide for setting up print-ready artwork files.',
  tag: 'artwork'
}, {
  title: 'Colour Specification Sheet',
  format: 'PDF',
  size: '1.1 MB',
  description: 'Pantone, CMYK, and RAL colour matching guidelines.',
  tag: 'artwork'
}, {
  title: 'File Format Requirements',
  format: 'PDF',
  size: '0.8 MB',
  description: 'Accepted file types, resolution, and bleed requirements.',
  tag: 'artwork'
}, {
  title: 'Label Template Pack',
  format: 'ZIP',
  size: '8.2 MB',
  description: 'AI, EPS, and PDF templates for common label sizes.',
  tag: 'templates'
}, {
  title: 'Nameplate Template Pack',
  format: 'ZIP',
  size: '5.4 MB',
  description: 'Standard nameplate templates in AI and EPS formats.',
  tag: 'templates'
}, {
  title: 'Asset Label Template',
  format: 'PDF',
  size: '1.2 MB',
  description: 'Ready-to-use asset label templates with barcode zones.',
  tag: 'templates'
}];
const technicalSheets = [{
  title: 'Asset Labels – Technical Data Sheet',
  product: 'Asset Labels',
  format: 'PDF',
  size: '1.3 MB',
  tag: 'technical'
}, {
  title: 'Tamper Evident Labels – Technical Data Sheet',
  product: 'Security Labels',
  format: 'PDF',
  size: '1.1 MB',
  tag: 'technical'
}, {
  title: 'Metal Nameplates – Technical Data Sheet',
  product: 'Nameplates',
  format: 'PDF',
  size: '1.5 MB',
  tag: 'technical'
}, {
  title: 'Polyester Labels – Material Specification',
  product: 'Materials',
  format: 'PDF',
  size: '0.9 MB',
  tag: 'technical'
}, {
  title: 'Polycarbonate Labels – Material Specification',
  product: 'Materials',
  format: 'PDF',
  size: '0.9 MB',
  tag: 'technical'
}, {
  title: 'Vinyl Labels – Material Specification',
  product: 'Materials',
  format: 'PDF',
  size: '0.7 MB',
  tag: 'technical'
}];
const blogPosts = [{
  category: 'Guide',
  title: 'Choosing the right label material for your environment',
  description: 'A practical guide to selecting materials based on temperature, chemical exposure, and surface type.',
  readTime: '5 min read',
  image: 'https://cdn.magicpatterns.com/uploads/tUEr7Zfb8BdMnYMRBFx6SN/sample.png',
  tag: 'blog'
}, {
  category: 'Compliance',
  title: 'Industrial labelling standards: What you need to know',
  description: 'An overview of BS EN, ISO, and sector-specific labelling compliance requirements.',
  readTime: '8 min read',
  image: 'https://cdn.magicpatterns.com/uploads/dympUc8nfN6AgL4ARo7EBA/sample2.webp',
  tag: 'blog'
}, {
  category: 'Technical',
  title: 'Barcode and QR code label best practices',
  description: 'How to ensure your barcodes scan reliably across different environments and scanning equipment.',
  readTime: '6 min read',
  image: 'https://cdn.magicpatterns.com/uploads/4GroWq4twSqQJRdxL7S1mT/sample_3.webp',
  tag: 'blog'
}];
export function ResourcesPage({
  onOpenSampleModal
}: ResourcesPageProps) {
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setUploadedFile(e.target.files[0]);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) setUploadedFile(e.dataTransfer.files[0]);
  };
  const showArtwork = activeFilter === 'all' || activeFilter === 'artwork' || activeFilter === 'templates';
  const showTechnical = activeFilter === 'all' || activeFilter === 'technical';
  const showBlog = activeFilter === 'all' || activeFilter === 'blog';
  const showUpload = activeFilter === 'all' || activeFilter === 'artwork';
  const filteredArtwork = artworkGuides.filter(g => activeFilter === 'all' || g.tag === activeFilter);
  const filteredTechnical = technicalSheets.filter(s => activeFilter === 'all' || s.tag === activeFilter);
  return <div className="min-h-screen bg-white font-sans text-[#0A1628] selection:bg-[#0052CC] selection:text-white overflow-x-hidden" data-id="element-1179">
      <Header onOpenSampleModal={onOpenSampleModal} data-id="element-1180" />

      <main className="pt-[135px] md:pt-[145px]" data-id="element-1181">
        {/* Hero */}
        <section className="py-14 md:py-20 bg-white border-b border-slate-100" data-id="element-1182">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1183">
            <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8" data-id="element-1184">
              <a href="/" className="hover:text-[#0052CC] transition-colors" data-id="element-1185">
                Home
              </a>
              <span data-id="element-1186">/</span>
              <span className="text-[#0A1628] font-medium" data-id="element-1187">Resources</span>
            </nav>
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} data-id="element-1188">
              <h1 className="text-4xl md:text-5xl font-extrabold text-[#0A1628] tracking-tight leading-tight mb-4" data-id="element-1189">
                Resource library
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed mb-8 max-w-xl" data-id="element-1190">
                Artwork guidelines, technical data sheets, templates, and
                industry insights — all free to download.
              </p>
              <div className="relative max-w-md" data-id="element-1191">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-1192" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search resources..." className="w-full pl-11 pr-5 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/15 focus:border-[#0052CC] transition-all" data-id="element-1193" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Filter Pills */}
        <div className="sticky top-[125px] z-30 bg-white border-b border-slate-100" data-id="element-1194">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-3" data-id="element-1195">
            <div className="flex items-center gap-2 overflow-x-auto" data-id="element-1196">
              {filters.map(filter => <button key={filter.key} onClick={() => setActiveFilter(filter.key)} className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${activeFilter === filter.key ? 'bg-[#0A1628] text-white border-[#0A1628]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`} data-id="element-1197">
                  {filter.label}
                </button>)}
            </div>
          </div>
        </div>

        {/* Artwork & Templates */}
        <AnimatePresence data-id="element-1198">
          {showArtwork && filteredArtwork.length > 0 && <motion.section initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="py-14 md:py-18 bg-white" data-id="element-1199">
              <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1200">
                <h2 className="text-2xl font-bold text-[#0A1628] mb-2" data-id="element-1201">
                  {activeFilter === 'templates' ? 'Templates' : 'Artwork guidelines & templates'}
                </h2>
                <p className="text-slate-500 text-sm mb-8" data-id="element-1202">
                  Download our guides to prepare print-ready files
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1203">
                  {filteredArtwork.map((guide, index) => <motion.div key={guide.title} initial={{
                opacity: 0,
                y: 8
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.05
              }} className="flex items-center gap-4 p-4 border border-slate-200 rounded-xl bg-white hover:border-slate-300 transition-colors" data-id="element-1204">
                      <div className="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1205">
                        <FileText className="h-5 w-5 text-slate-500" data-id="element-1206" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1207">
                        <p className="font-semibold text-[#0A1628] text-sm" data-id="element-1208">
                          {guide.title}
                        </p>
                        <p className="text-slate-500 text-xs mt-0.5" data-id="element-1209">
                          {guide.description}
                        </p>
                        <span className="text-xs text-slate-400" data-id="element-1210">
                          {guide.format} · {guide.size}
                        </span>
                      </div>
                      <a href="#" className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg hover:bg-[#0A1628] hover:text-white hover:border-[#0A1628] transition-all" data-id="element-1211">
                        <Download className="h-3.5 w-3.5" data-id="element-1212" />
                        Download
                      </a>
                    </motion.div>)}
                </div>
              </div>
            </motion.section>}
        </AnimatePresence>

        {/* Artwork Upload */}
        <AnimatePresence data-id="element-1213">
          {showUpload && <motion.section initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="py-14 bg-slate-50 border-y border-slate-100" data-id="element-1214">
              <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1215">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1216">
                  <div data-id="element-1217">
                    <h2 className="text-2xl font-bold text-[#0A1628] mb-2" data-id="element-1218">
                      Upload your artwork
                    </h2>
                    <p className="text-slate-500 text-sm mb-8" data-id="element-1219">
                      Send us your design files for review and proofing. We'll
                      send a digital proof within 1 working day.
                    </p>

                    <div onDragOver={e => {
                  e.preventDefault();
                  setIsDragging(true);
                }} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop} className={`border-2 border-dashed rounded-xl p-10 text-center transition-all ${isDragging ? 'border-[#0052CC] bg-[#0052CC]/5' : 'border-slate-200 bg-white'}`} data-id="element-1220">
                      {uploadedFile ? <div data-id="element-1221">
                          <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-1222">
                            <File className="h-6 w-6 text-green-600" data-id="element-1223" />
                          </div>
                          <p className="font-semibold text-[#0A1628] text-sm mb-1" data-id="element-1224">
                            {uploadedFile.name}
                          </p>
                          <p className="text-xs text-slate-500 mb-3" data-id="element-1225">
                            {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                          <button onClick={() => setUploadedFile(null)} className="text-xs text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1226">
                            Remove
                          </button>
                        </div> : <div data-id="element-1227">
                          <div className="h-12 w-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-1228">
                            <Upload className="h-6 w-6 text-slate-400" data-id="element-1229" />
                          </div>
                          <p className="font-semibold text-[#0A1628] text-sm mb-1" data-id="element-1230">
                            Drag and drop your file here
                          </p>
                          <p className="text-xs text-slate-500 mb-5" data-id="element-1231">
                            AI, EPS, PDF, PNG, JPG — up to 50MB
                          </p>
                          <label className="cursor-pointer inline-flex items-center px-5 py-2.5 bg-[#0A1628] text-white text-sm font-semibold rounded-lg hover:bg-[#0052CC] transition-colors" data-id="element-1232">
                            <Upload className="mr-2 h-4 w-4" data-id="element-1233" />
                            Choose file
                            <input type="file" className="hidden" onChange={handleFileChange} accept=".ai,.eps,.pdf,.png,.jpg,.jpeg" data-id="element-1234" />
                          </label>
                        </div>}
                    </div>
                    {uploadedFile && <button className="mt-3 w-full py-3 bg-[#0052CC] text-white font-semibold rounded-lg hover:bg-[#0040A0] transition-colors flex items-center justify-center text-sm" data-id="element-1235">
                        Submit for review{' '}
                        <ArrowRight className="ml-2 h-4 w-4" data-id="element-1236" />
                      </button>}
                  </div>

                  <div className="hidden lg:block" data-id="element-1237">
                    <div className="rounded-xl overflow-hidden aspect-[4/3]" data-id="element-1238">
                      <img src="https://cdn.magicpatterns.com/uploads/dympUc8nfN6AgL4ARo7EBA/sample2.webp" alt="Label samples" className="w-full h-full object-cover" data-id="element-1239" />
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-1240">
                      {['Accepts AI, EPS, PDF', 'Max 50MB', 'Proof in 24hrs'].map(item => <div key={item} className="bg-white rounded-lg p-3 text-center border border-slate-100" data-id="element-1241">
                          <p className="text-xs text-slate-600 font-medium" data-id="element-1242">
                            {item}
                          </p>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>}
        </AnimatePresence>

        {/* Technical Sheets */}
        <AnimatePresence data-id="element-1243">
          {showTechnical && filteredTechnical.length > 0 && <motion.section initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="py-14 md:py-18 bg-white" data-id="element-1244">
              <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1245">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1246">
                  <div data-id="element-1247">
                    <h2 className="text-2xl font-bold text-[#0A1628] mb-2" data-id="element-1248">
                      Technical data sheets
                    </h2>
                    <p className="text-slate-500 text-sm leading-relaxed mb-6" data-id="element-1249">
                      Product specifications and material data for all our
                      products
                    </p>
                    <div className="rounded-xl overflow-hidden aspect-[4/3]" data-id="element-1250">
                      <img src="https://cdn.magicpatterns.com/uploads/4GroWq4twSqQJRdxL7S1mT/sample_3.webp" alt="Technical samples" className="w-full h-full object-cover" data-id="element-1251" />
                    </div>
                  </div>

                  <div className="lg:col-span-2" data-id="element-1252">
                    <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden" data-id="element-1253">
                      {filteredTechnical.map((sheet, index) => <motion.div key={sheet.title} initial={{
                    opacity: 0
                  }} whileInView={{
                    opacity: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    delay: index * 0.04
                  }} className="flex items-center gap-4 px-5 py-4 bg-white hover:bg-slate-50 transition-colors" data-id="element-1254">
                          <div className="h-9 w-9 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1255">
                            <FileText className="h-4 w-4 text-slate-500" data-id="element-1256" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1257">
                            <p className="font-semibold text-[#0A1628] text-sm" data-id="element-1258">
                              {sheet.title}
                            </p>
                            <p className="text-xs text-slate-400" data-id="element-1259">
                              {sheet.product} · {sheet.format} · {sheet.size}
                            </p>
                          </div>
                          <a href="#" className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-[#0A1628] hover:text-white hover:border-[#0A1628] transition-all" data-id="element-1260">
                            <Download className="h-3.5 w-3.5" data-id="element-1261" />
                            <span className="hidden sm:inline" data-id="element-1262">Download</span>
                          </a>
                        </motion.div>)}
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>}
        </AnimatePresence>

        {/* Blog & Insights */}
        <AnimatePresence data-id="element-1263">
          {showBlog && <motion.section initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="py-14 md:py-20 bg-slate-50 border-t border-slate-100" data-id="element-1264">
              <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1265">
                <div className="flex items-end justify-between mb-10" data-id="element-1266">
                  <div data-id="element-1267">
                    <h2 className="text-2xl font-bold text-[#0A1628] mb-1" data-id="element-1268">
                      Blog & Insights
                    </h2>
                    <p className="text-slate-500 text-sm" data-id="element-1269">
                      Guides, compliance updates, and technical knowledge
                    </p>
                  </div>
                  <a href="#" className="hidden md:flex items-center gap-1 text-sm font-semibold text-[#0052CC] hover:text-[#0040A0] transition-colors" data-id="element-1270">
                    View all <ArrowRight className="h-4 w-4" data-id="element-1271" />
                  </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1272">
                  {blogPosts.map((post, index) => <motion.a key={post.title} href="#" initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all" data-id="element-1273">
                      <div className="aspect-[16/9] overflow-hidden bg-slate-100" data-id="element-1274">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1275" />
                      </div>
                      <div className="p-5" data-id="element-1276">
                        <div className="flex items-center gap-2 mb-3" data-id="element-1277">
                          <span className="text-xs font-semibold text-[#0052CC]" data-id="element-1278">
                            {post.category}
                          </span>
                          <span className="text-slate-300" data-id="element-1279">·</span>
                          <span className="flex items-center gap-1 text-xs text-slate-400" data-id="element-1280">
                            <Clock className="h-3 w-3" data-id="element-1281" /> {post.readTime}
                          </span>
                        </div>
                        <h3 className="text-sm font-bold text-[#0A1628] mb-2 leading-snug group-hover:text-[#0052CC] transition-colors" data-id="element-1282">
                          {post.title}
                        </h3>
                        <p className="text-slate-500 text-xs leading-relaxed mb-4" data-id="element-1283">
                          {post.description}
                        </p>
                        <span className="text-xs font-semibold text-[#0052CC] flex items-center" data-id="element-1284">
                          Read article{' '}
                          <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" data-id="element-1285" />
                        </span>
                      </div>
                    </motion.a>)}
                </div>
              </div>
            </motion.section>}
        </AnimatePresence>
      </main>

      <Footer data-id="element-1286" />
    </div>;
}